import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd, readDurationToken, removeWillChangeOnAnimationEnd } from "@theme/utilities";

const ARIA_EXPANDED = "aria-expanded";
const MENU_OPEN = "menu-open";
const SUBMENU_OPEN_CLASS = "menu-drawer--has-submenu-opened";
// Safety upper bound for the close transition. The CSS uses --spring-d220-b0-duration
// (~382ms); the buffer covers easing tail and slow devices. Read at module load from
// the same token plus a 120ms safety margin so CSS token changes stay synchronized
// with this fallback. If onAnimationEnd
// never resolves (CSS transition not picked up by getAnimations, prefers-reduced-motion,
// etc.) we still finish.
const CLOSE_FALLBACK_MS = readDurationToken("--spring-d220-b0-duration", 382) + 120;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details", "menuDrawer"];

  /**
   * The inert attribute value each background sibling had before this drawer
   * opened. `null` means the drawer does not currently own an isolation cycle.
   *
   * @type {Map<HTMLElement, string | null> | null}
   */
  #backgroundInertSnapshot = null;

  /** Invalidates animation-delayed root drawer work without canceling submenu cleanup. */
  #rootTransitionCycle = 0;

  /** Invalidates animation-delayed submenu work without canceling root drawer cleanup. */
  #submenuTransitionCycle = 0;

  /**
   * A same-document hash navigation does not unload the document, so the
   * normal page lifecycle cannot tear the drawer down. Wait for the browser's
   * successful hash change instead of closing on the initiating click: if an
   * app or merchant script prevents that click, the open drawer remains fully
   * reversible.
   */
  #onHashChange = () => {
    if (this.isOpen) {
      this.close().catch(() => null);
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = event => {
    if (event.key !== "Escape") {
      return;
    }

    // With the drawer already closed there is nothing to return from, so the menu button keeps its ring as it is.
    const details = this.#getDetailsElement(event);
    this.#close(details, details.hasAttribute("open") ? this.#ringOnEscape : undefined);
  };

  /** Whether focus returns with a ring after Escape: the drawer was opened from the keyboard, or Tab used in it. */
  #ringOnEscape = false;

  /** @param {KeyboardEvent} event */
  #onKeyDown = event => {
    if (event.key === "Tab") {
      this.#ringOnEscape = true;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.addEventListener("keydown", this.#onKeyDown);
    globalThis.addEventListener("hashchange", this.#onHashChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.removeEventListener("keydown", this.#onKeyDown);
    globalThis.removeEventListener("hashchange", this.#onHashChange);
    this.#rootTransitionCycle += 1;
    this.#submenuTransitionCycle += 1;

    if (this.#backgroundInertSnapshot) {
      const rootDetails = this.refs.details;
      const ownedScrollLock = rootDetails.hasAttribute("open") && rootDetails.hasAttribute("scroll-lock");
      removeTrapFocus();
      this.refs.menuDrawer.classList.remove(SUBMENU_OPEN_CLASS);
      const openDetails = this.querySelectorAll("details[open]:not(accordion-custom > details)");
      for (const element of openDetails) {
        reset(/** @type {HTMLDetailsElement} */ (element));
      }

      if (ownedScrollLock && !document.querySelector("details[scroll-lock][open]")) {
        document.documentElement.removeAttribute("scroll-lock");
      }

      this.#restoreBackgroundInert();
    }
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) {
      return;
    }

    if (details !== this.refs.details && !this.refs.details.classList.contains(MENU_OPEN)) {
      reset(details);
      return;
    }

    if (details === this.refs.details) {
      this.#ringOnEscape = summary.matches(":focus-visible");
    }
    this.#isolateBackground();
    const openCycle = this.#nextTransitionCycle(details);
    summary.setAttribute(ARIA_EXPANDED, "true");

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (!this.#isCurrentTransition(details, openCycle) || !this.isConnected) {
        return;
      }

      if (details !== this.refs.details && !this.refs.details.classList.contains(MENU_OPEN)) {
        reset(details);
        return;
      }

      details.classList.add(MENU_OPEN);

      if (target) {
        this.refs.menuDrawer.classList.add(SUBMENU_OPEN_CLASS);
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");
      onAnimationEnd(
        drawer || details,
        () => {
          const rootCanContainFocus = details === this.refs.details || this.refs.details.classList.contains(MENU_OPEN);
          const rootHasNewerSubmenu =
            details === this.refs.details && this.refs.menuDrawer.classList.contains(SUBMENU_OPEN_CLASS);
          if (
            this.#isCurrentTransition(details, openCycle) &&
            this.isConnected &&
            rootCanContainFocus &&
            !rootHasNewerSubmenu &&
            details.classList.contains(MENU_OPEN)
          ) {
            const focusContainer = details === this.refs.details ? this.refs.menuDrawer : details;
            trapFocus(focusContainer);
            if (details === this.refs.details) {
              const closeButton = this.refs.menuDrawer.querySelector(".menu-drawer__close-button--panel");
              if (closeButton instanceof HTMLElement) {
                closeButton.focus();
              }
            } else {
              // Localization owns its initial country/search choice; the header owns
              // when focus can enter the submenu after its transition and focus trap.
              const localizationForm = /** @type {(HTMLElement & { focusSearchInput?: () => void }) | null} */ (
                details.querySelector("localization-form-component")
              );
              localizationForm?.focusSearchInput?.();
              const submenuFocusTarget = localizationForm?.contains(document.activeElement)
                ? null
                : (details.querySelector(".menu-drawer__back-button") ?? details.querySelector("summary"));
              if (submenuFocusTarget instanceof HTMLElement) {
                submenuFocusTarget.focus();
              }
            }
          }
        },
        { subtree: false },
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   * @returns {Promise<void>} Resolves when the close animation has finished.
   */
  close() {
    return this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * @param {HTMLDetailsElement} details
   * @param {boolean} [ring] - After Escape, whether focus returns with a ring; otherwise the browser decides.
   * @returns {Promise<void>} Resolves once the drawer/submenu has finished animating
   *   out and the open state has been reset.
   */
  #close(details, ring) {
    const closeCycle = this.#nextTransitionCycle(details);
    const summary = details.querySelector("summary");

    if (!summary) {
      if (details === this.refs.details) {
        removeTrapFocus();
        this.#restoreBackgroundInert();
      }
      return Promise.resolve();
    }

    const shouldRestoreLauncherFocus =
      details === this.refs.details &&
      document.activeElement instanceof Node &&
      details.contains(document.activeElement);

    summary.setAttribute(ARIA_EXPANDED, "false");
    details.classList.remove(MENU_OPEN);
    if (details === this.refs.details) {
      this.refs.menuDrawer.classList.remove(SUBMENU_OPEN_CLASS);
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree.
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox.
    const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");
    const animationTarget = drawer || details;

    const finalize = () => {
      if (!this.#isCurrentTransition(details, closeCycle)) {
        return;
      }

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]:not(accordion-custom > details)");
        openDetails.forEach(element => reset(/** @type {HTMLDetailsElement} */ (element)));
        if (shouldRestoreLauncherFocus && summary.isConnected) {
          focusWithRing(summary, ring);
        }
        this.#restoreBackgroundInert();
      } else if (this.refs.details.hasAttribute("open") && this.refs.details.classList.contains(MENU_OPEN)) {
        this.refs.menuDrawer.classList.toggle(
          SUBMENU_OPEN_CLASS,
          Boolean(this.refs.menuDrawer.querySelector("details.menu-open > .menu-drawer__submenu")),
        );
        trapFocus(this.refs.menuDrawer);
        if (summary.isConnected) {
          focusWithRing(summary, ring);
        }
      } else {
        removeTrapFocus();
      }
    };

    return new Promise(resolve => {
      // Wait one frame so the CSS transition triggered by removing MENU_OPEN is
      // registered on the Animation timeline before getAnimations() queries it.
      // Without this rAF, getAnimations can return [] synchronously, onAnimationEnd
      // resolves on the same tick, and the drawer collapses without animating.
      requestAnimationFrame(() => {
        let settled = false;
        const done = () => {
          if (settled) {
            return;
          }
          settled = true;
          finalize();
          resolve();
        };

        // Safety net: if no animations are picked up (reduced motion, edge browser,
        // race), close anyway so the drawer can never be left in a half-open state.
        const fallbackId = globalThis.setTimeout(done, CLOSE_FALLBACK_MS);

        onAnimationEnd(
          animationTarget,
          () => {
            globalThis.clearTimeout(fallbackId);
            done();
          },
          { subtree: false },
        );
      });
    });
  }

  /**
   * Advance the transition generation for the root drawer or the shared submenu scope.
   * Keeping these scopes independent prevents a late submenu action from canceling
   * root-close cleanup while still allowing the newest submenu transition to win.
   *
   * @param {HTMLDetailsElement} details
   * @returns {number}
   */
  #nextTransitionCycle(details) {
    if (details === this.refs.details) {
      this.#rootTransitionCycle += 1;
      return this.#rootTransitionCycle;
    }

    this.#submenuTransitionCycle += 1;
    return this.#submenuTransitionCycle;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {number} cycle
   * @returns {boolean}
   */
  #isCurrentTransition(details, cycle) {
    return details === this.refs.details ? cycle === this.#rootTransitionCycle : cycle === this.#submenuTransitionCycle;
  }

  /**
   * Make every element outside the drawer-to-body ancestor path inert. Recording
   * each sibling before mutating it makes the operation synchronous and lets a
   * later close restore pre-existing inert states without guessing ownership.
   */
  #isolateBackground() {
    if (this.#backgroundInertSnapshot || !document.body.contains(this)) {
      return;
    }

    const snapshot = new Map();
    this.#backgroundInertSnapshot = snapshot;

    let ancestor = this.parentElement;
    while (ancestor) {
      for (const sibling of ancestor.children) {
        if (sibling instanceof HTMLElement && !sibling.contains(this)) {
          snapshot.set(sibling, sibling.getAttribute("inert"));
          sibling.setAttribute("inert", "");
        }
      }

      if (ancestor === document.body) {
        break;
      }
      ancestor = ancestor.parentElement;
    }
  }

  /** Restore the exact inert attribute state captured for this open cycle. */
  #restoreBackgroundInert() {
    const snapshot = this.#backgroundInertSnapshot;
    if (!snapshot) {
      return;
    }

    for (const [element, inertValue] of snapshot) {
      if (inertValue === null) {
        element.removeAttribute("inert");
      } else {
        element.setAttribute("inert", inertValue);
      }
    }

    this.#backgroundInertSnapshot = null;
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll(".menu-drawer__animated-element")
    );
    allAnimated.forEach(element => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll("accordion-custom .details-content");

    content.forEach(element => {
      if (element instanceof HTMLElement) {
        element.classList.add("details-content--no-animation");
      }
    });
    setTimeout(() => {
      content.forEach(element => {
        if (element instanceof HTMLElement) {
          element.classList.remove("details-content--no-animation");
        }
      });
    }, 100);
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove(MENU_OPEN);
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute(ARIA_EXPANDED, "false");
}

/**
 * Focuses `element` with or without its ring; with `ring` undefined the browser decides. The browser only redraws
 * the ring on a fresh focus, so an element that already has focus is blurred first.
 *
 * @param {HTMLElement} element
 * @param {boolean} [ring]
 */
function focusWithRing(element, ring) {
  if (ring === undefined) {
    element.focus();
    return;
  }

  if (document.activeElement === element && element.matches(":focus-visible") !== ring) {
    element.blur();
  }
  element.focus({ focusVisible: ring });
}
